import ButtonViewModelT from '@/ViewModels/Controls/ButtonViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ControlViewModelBuilder from '@/Builder/ControlViewModelBuilder';
import { IButtonViewModelConstructorParameter } from '#/DefaultViewExtensionInterfaces';
import ImageViewModelBuilder from '@/Builder/ImageViewModelBuilder';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import LocatorHelper from '@/Utility/LocatorHelper';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

export default class ButtonViewModelBuilder<T = null> extends ControlViewModelBuilder<ButtonViewModelT, T>{

    private imageBuilder: ImageViewModelBuilder<this>;
    private labelBuilder: LabelViewModelBuilder<this>;

    protected properties: IButtonViewModelConstructorParameter;

    public constructor(parent?: T) {
        super(parent);
    }

    public withIsPrimaryButton(isPrimaryButton: ObservableOrDefault<boolean>): this {
        this.properties.isPrimaryButton = isPrimaryButton;
        return this;
    }

    public withImage(): ImageViewModelBuilder<this> {
        if (this.imageBuilder == null)
            this.imageBuilder = new ImageViewModelBuilder<this>(this);

        return this.imageBuilder;
    }

    public withLabel(): LabelViewModelBuilder<this> {
        if (this.labelBuilder == null)
            this.labelBuilder = new LabelViewModelBuilder<this>(this);

        return this.labelBuilder;
    }

    public withOnClick(onClick: Function): this {
        this.properties.onClick = onClick;
        return this;
    }
    
    public withTextColor(textColor: ObservableOrDefault<string>): this {
        this.properties.textColor = textColor;
        return this;
    }

    public withBackgroundColor(backgroundColor: ObservableOrDefault<string>): this {
        this.properties.backgroundColor = backgroundColor;
        return this;
    }
    
    public withBorderColor(borderColor: ObservableOrDefault<string>): this {
        this.properties.borderColor = borderColor;
        return this;
    }
    
    public withHoverColor(hoverColor: ObservableOrDefault<string>): this {
        this.properties.hoverColor = hoverColor;
        return this;
    }
    
    public updateProperties(properties: Partial<IButtonViewModelConstructorParameter>): this {
        $.extend(this.properties, properties);
        return this;
    }

    public initForOkayButton(parentLocator: ObservableOrDefault<string>, onClick: Function): this {
        return this.initForDefaultButton(parentLocator, onClick, false, 'OKAY', 'ok', GlobalConfiguration.inlineImages.WINDOW_OK);
    }

    public initForCancelButton(parentLocator: ObservableOrDefault<string>, onClick: Function): this {
        return this.initForDefaultButton(parentLocator, onClick, false, 'CANCEL', 'cancel', GlobalConfiguration.inlineImages.WINDOW_CANCEL);
    }

    private initForDefaultButton(parentLocator: ObservableOrDefault<string>, onClick: Function, isPrimaryButton: boolean,
        ergName: string, locatorName: string, image: string): this {
        return this.updateProperties({
            parentLocator: parentLocator,
            locator: LocatorHelper.buildLocator('button', locatorName),
            isPrimaryButton: isPrimaryButton,
        })
            .withImage()
            .updateProperties({
                parentLocator: parentLocator,
                locator: LocatorHelper.buildLocator('button', locatorName, 'image'),
                source: image,
            })
            .withAlternativeTextErgName(ergName)
            .done()
            .withLabel()
            .updateProperties({
                parentLocator: parentLocator,
                locator: LocatorHelper.buildLocator('button', locatorName, 'label'),
                ergName: ergName
            })
            .done()
            .withOnClick(onClick);
    }

    /**
     * @override
     */
    protected preBuild(): void {
        if (this.imageBuilder != null)
            this.properties.image = this.imageBuilder.build();

        if (this.labelBuilder != null)
            this.properties.label = this.labelBuilder.build();
    }

    /**
     * @override
     */
    protected buildInternal(): ButtonViewModelT {
        const ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModelT>('ButtonViewModel');

        return new ButtonViewModel(this.properties);
    }
}